"use client";

import { useState } from "react";
import { Minus, Plus } from "lucide-react";
import JsonLd from "./JsonLd";
import { getFAQSchema } from "@aicodepro/web-builder-utils";

interface QnAItem {
  question: string;
  answer: string;
}

interface FAQProps {
  data: {
    id: string;
    title: string;
    description: string;
    faqs: QnAItem[];
    isLive: boolean;
    useH1Tag?: boolean;
  };
}

function QnAComponent({
  question,
  answer,
  onToggle,
  isOpen,
}: QnAItem & { onToggle: () => void; isOpen: boolean }) {
  return (
    <div
      className={`mb-4 overflow-hidden rounded-2xl transition-all duration-300 border ${
        isOpen
          ? "bg-white shadow-xl shadow-black/5 border-[var(--secondary)]/20"
          : "bg-white shadow-sm border-gray-100 hover:border-[var(--secondary)]/30"
      }`}
    >
      <button
  onClick={onToggle}
  className="flex w-full items-center justify-between gap-4 p-5 sm:p-6 text-left focus:outline-none group"
  aria-expanded={isOpen}
>
  <span
    className={`text-[15px] sm:text-[18px] font-bold transition-colors duration-300 ${
      isOpen
        ? "text-[var(--secondary)]"  
        : "text-[#0f2347] group-hover:text-[var(--secondary)]" 
    }`}
  >
    {question}
  </span>

  <div
    className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full transition-all duration-300 ${
      isOpen
        ? "bg-[var(--secondary)] text-white rotate-0"
        : "bg-gray-100 text-gray-400 rotate-90"
    }`}
  >
    {isOpen ? (
      <Minus className="h-4 w-4" />
    ) : (
      <Plus className="h-4 w-4" />
    )}
  </div>
</button>


      {/* CSS GRID ANIMATION LOGIC:
          Transitioning grid-template-rows from 0fr to 1fr allows the 
          browser to animate to the 'auto' height of the content.
      */}
      <div
        className={`grid transition-all duration-500 ease-in-out ${
          isOpen ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0"
        }`}
      >
        <div className="overflow-hidden">
          <div className="px-5 pb-6 sm:px-6 sm:pb-8">
            <p className="text-[14px] sm:text-[16px] leading-relaxed text-gray-500 font-light">
              {answer}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function FAQ({ data }: FAQProps) {
  const [openIndex, setOpenIndex] = useState<number | null>(0); // Default first item open

  const handleToggle = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  if (!data?.isLive) return null;

  return (
    <section className="w-full  py-20 sm:py-28 px-6">
      <JsonLd schema={getFAQSchema(data.faqs)} />

      <div className="mx-auto max-w-3xl">
        {/* Editorial Header */}
        <div className="mb-16 text-center">
          <span
            className="text-[var(--secondary)] uppercase tracking-[0.3em] text-[10px] md:text-xs font-black mb-4 block"
            style={{
              color: "var(--secondary)",
            }}
          >
            FaQs
          </span>

          {data?.useH1Tag ? (
            <h1 className=" text-4xl font-bold tracking-tight text-[#1A1A1A] sm:text-5xl mb-6 italic">
              {data?.title}
            </h1>
          ) : (
            <h2 className=" text-4xl font-bold tracking-tight text-[#1A1A1A] sm:text-5xl mb-6 italic">
              {data?.title}
            </h2>
          )}

          {data?.description && (
            <p className="mx-auto max-w-2xl text-base text-gray-500 font-light leading-relaxed">
              {data?.description}
            </p>
          )}
        </div>

        {/* FAQ List */}
        <div className="flex flex-col space-y-2">
          {data?.faqs.map((faq, index) => (
            <QnAComponent
              key={index}
              question={faq.question}
              answer={faq.answer}
              isOpen={openIndex === index}
              onToggle={() => handleToggle(index)}
            />
          ))}
        </div>

        {/* Branding Footer Accent (Optional, matches your top picks style) */}
        {/* <div className="mt-20 w-full flex justify-center" aria-hidden="true">
          <div className="h-px w-full max-w-xs bg-gray-200 flex items-center justify-center">
            <div className="bg-[#F9F7F2] px-6">
              <span className="text-[10px] font-black uppercase tracking-[0.6em] text-gray-300">
                SUPPORT
              </span>
            </div>
          </div>
        </div> */}
      </div>
    </section>
  );
}
